import type { LoomAuthoringDocument } from "../features/parametric-geometry/loomAuthoringModel";
import { loomAuthoringDocumentStateId } from "../features/parametric-geometry/loomAuthoringHistory";
import {
  parseLoomJunctionFairingIntent,
  type LoomJunctionFairingIntent,
  type LoomJunctionFairingStation,
} from "../features/parametric-geometry/loomJunctionFairingIntent";
import {
  deriveLoomAircraftOccurrenceRebuildDefinition,
  parseLoomAuthoritativeRebuildRequestV4,
  type LoomAircraftOccurrenceRebuildDefinition,
} from "./loomRebuildV4";

export const LOOM_REBUILD_SCHEMA_V5 = "loom.authoritative-rebuild-request/v5" as const;
export const LOOM_REBUILD_DEFINITION_SCHEMA_V5 = "loom.aircraft-occurrence-rebuild-definition/v2" as const;
export const LOOM_REBUILD_EVALUATOR_V5 = "loom.native-aircraft-occurrence-transition-union/v1" as const;
export const LOOM_REBUILD_TOPOLOGY_V5 = "ordered-aircraft-occurrence-transition-union" as const;
export const LOOM_TRANSITION_SCHEMA_V2 = "loom.aircraft-junction-transition-definition/v2" as const;

export interface LoomAircraftJunctionTransitionV2 {
  schema: typeof LOOM_TRANSITION_SCHEMA_V2;
  transitionId: string;
  unionGroupId: string;
  parentA: { occurrenceId: string; patchId: string };
  parentB: { occurrenceId: string; patchId: string };
  theoreticalSeamId: string;
  construction: LoomJunctionFairingIntent["construction"];
}

export interface LoomAircraftOccurrenceTransitionRebuildDefinition {
  schema: typeof LOOM_REBUILD_DEFINITION_SCHEMA_V5;
  sourceDefinitions: LoomAircraftOccurrenceRebuildDefinition["sourceDefinitions"];
  occurrences: LoomAircraftOccurrenceRebuildDefinition["occurrences"];
  unionGroups: LoomAircraftOccurrenceRebuildDefinition["unionGroups"];
  transitions: LoomAircraftJunctionTransitionV2[];
}

export interface LoomAuthoritativeRebuildRequestV5 {
  schema: typeof LOOM_REBUILD_SCHEMA_V5;
  requestId: string;
  sourceDefinition: { documentId: string; revision: number; stateId: string };
  evaluatorId: typeof LOOM_REBUILD_EVALUATOR_V5;
  topology: typeof LOOM_REBUILD_TOPOLOGY_V5;
  definition: LoomAircraftOccurrenceTransitionRebuildDefinition;
}

type Row = Record<string, unknown>;
const TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,159}$/;

function object(value: unknown, path: string): Row {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Row;
}

function exact(value: Row, keys: readonly string[], path: string): void {
  const actual = Object.keys(value);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${path} has an unsupported field set`);
  }
}

function token(value: unknown, path: string): string {
  if (typeof value !== "string" || !TOKEN.test(value)) throw new Error(`${path} is invalid`);
  return value;
}

function parsedConstruction(value: unknown): LoomJunctionFairingIntent["construction"] {
  return parseLoomJunctionFairingIntent({
    schema: "loom.junction-fairing-intent/v1",
    transitionId: "parser-placeholder",
    construction: value,
  }).construction;
}

function parseTransition(value: unknown, index: number): LoomAircraftJunctionTransitionV2 {
  const path = `definition.transitions[${index}]`;
  const row = object(value, path);
  exact(row, ["schema", "transitionId", "unionGroupId", "parentA", "parentB",
    "theoreticalSeamId", "construction"], path);
  if (row.schema !== LOOM_TRANSITION_SCHEMA_V2) throw new Error(`${path}.schema is unsupported`);
  const parent = (value: unknown, label: string) => {
    const item = object(value, label);
    exact(item, ["occurrenceId", "patchId"], label);
    return { occurrenceId: token(item.occurrenceId, `${label}.occurrenceId`), patchId: token(item.patchId, `${label}.patchId`) };
  };
  const parentA = parent(row.parentA, `${path}.parentA`);
  const parentB = parent(row.parentB, `${path}.parentB`);
  if (parentA.occurrenceId === parentB.occurrenceId && parentA.patchId === parentB.patchId) {
    throw new Error(`${path} parents must be distinct`);
  }
  return {
    schema: LOOM_TRANSITION_SCHEMA_V2,
    transitionId: token(row.transitionId, `${path}.transitionId`),
    unionGroupId: token(row.unionGroupId, `${path}.unionGroupId`),
    parentA, parentB,
    theoreticalSeamId: token(row.theoreticalSeamId, `${path}.theoreticalSeamId`),
    construction: parsedConstruction(row.construction),
  };
}

export function deriveLoomAircraftOccurrenceTransitionRebuildDefinition(
  document: LoomAuthoringDocument,
): LoomAircraftOccurrenceTransitionRebuildDefinition | null {
  const base = deriveLoomAircraftOccurrenceRebuildDefinition(document);
  if (!base || base.unionGroups.length !== 1) return null;
  const sourceWingId = base.sourceDefinitions[0]?.definition.wing.sourceNodeId;
  const sourceWing = document.nodes.find((node) => node.id === sourceWingId
    && node.type === "primitive" && node.definition.primitive === "wing");
  if (!sourceWing || sourceWing.type !== "primitive" || sourceWing.definition.primitive !== "wing") return null;
  const fairing = sourceWing.definition.junctionFairing;
  if (!fairing) return null;
  const group = base.unionGroups[0]!;
  const properWings = base.occurrences.filter((row) => row.role === "wing" && row.parity === "proper");
  const fuselages = base.occurrences.filter((row) => row.role === "fuselage");
  if (properWings.length !== 1 || fuselages.length !== 1) return null;
  const parentA = properWings[0]!; const parentB = fuselages[0]!;
  const intent = parseLoomJunctionFairingIntent(fairing);
  return {
    schema: LOOM_REBUILD_DEFINITION_SCHEMA_V5,
    sourceDefinitions: base.sourceDefinitions,
    occurrences: base.occurrences,
    unionGroups: base.unionGroups,
    transitions: [{
      schema: LOOM_TRANSITION_SCHEMA_V2,
      transitionId: intent.transitionId,
      unionGroupId: group.unionGroupId,
      parentA: {
        occurrenceId: parentA.occurrenceId,
        patchId: `${parentA.occurrenceId}::${group.unionGroupId}:wing-trimmed`,
      },
      parentB: {
        occurrenceId: parentB.occurrenceId,
        patchId: `${parentB.occurrenceId}::${group.unionGroupId}:fuselage-trimmed`,
      },
      theoreticalSeamId: `${group.unionGroupId}:${parentA.occurrenceId}:${parentB.occurrenceId}`,
      construction: intent.construction,
    }],
  };
}

export function deriveLoomAuthoritativeRebuildRequestV5(
  document: LoomAuthoringDocument,
  requestId: string,
): LoomAuthoritativeRebuildRequestV5 | null {
  const definition = deriveLoomAircraftOccurrenceTransitionRebuildDefinition(document);
  if (!definition) return null;
  return parseLoomAuthoritativeRebuildRequestV5({
    schema: LOOM_REBUILD_SCHEMA_V5,
    requestId,
    sourceDefinition: {
      documentId: document.documentId,
      revision: document.revision,
      stateId: loomAuthoringDocumentStateId(document),
    },
    evaluatorId: LOOM_REBUILD_EVALUATOR_V5,
    topology: LOOM_REBUILD_TOPOLOGY_V5,
    definition,
  });
}

export function parseLoomAuthoritativeRebuildRequestV5(value: unknown): LoomAuthoritativeRebuildRequestV5 {
  const root = object(value, "v5 request");
  exact(root, ["schema", "requestId", "sourceDefinition", "evaluatorId", "topology", "definition"], "v5 request");
  if (root.schema !== LOOM_REBUILD_SCHEMA_V5 || root.evaluatorId !== LOOM_REBUILD_EVALUATOR_V5
      || root.topology !== LOOM_REBUILD_TOPOLOGY_V5) throw new Error("v5 request markers are unsupported");
  const definition = object(root.definition, "v5 definition");
  exact(definition, ["schema", "sourceDefinitions", "occurrences", "unionGroups", "transitions"], "v5 definition");
  if (definition.schema !== LOOM_REBUILD_DEFINITION_SCHEMA_V5
      || !Array.isArray(definition.transitions) || definition.transitions.length < 1
      || definition.transitions.length > 4) throw new Error("v5 transition table is invalid");
  const base = parseLoomAuthoritativeRebuildRequestV4({
    schema: "loom.authoritative-rebuild-request/v4",
    requestId: root.requestId,
    sourceDefinition: root.sourceDefinition,
    evaluatorId: "loom.native-aircraft-occurrence-union/v1",
    topology: "ordered-aircraft-occurrence-union",
    definition: {
      schema: "loom.aircraft-occurrence-rebuild-definition/v1",
      sourceDefinitions: definition.sourceDefinitions,
      occurrences: definition.occurrences,
      unionGroups: definition.unionGroups,
    },
  });
  const transitions = definition.transitions.map(parseTransition)
    .sort((left, right) => left.transitionId.localeCompare(right.transitionId));
  if (new Set(transitions.map(({ transitionId }) => transitionId)).size !== transitions.length) {
    throw new Error("v5 transition IDs must be unique");
  }
  const occurrenceById = new Map(base.definition.occurrences.map((row) => [row.occurrenceId, row]));
  const groupById = new Map(base.definition.unionGroups.map((row) => [row.unionGroupId, row]));
  const pairKeys = new Set<string>();
  transitions.forEach((transition) => {
    const group = groupById.get(transition.unionGroupId);
    const parents = [transition.parentA, transition.parentB];
    const parentRows = parents.map(({ occurrenceId }) => occurrenceById.get(occurrenceId));
    if (!group || parentRows.some((row) => !row)
        || parents.some(({ occurrenceId }) => !group.orderedOccurrenceIds.includes(occurrenceId))
        || new Set(parentRows.map((row) => row!.role)).size !== 2
        || !parentRows.some((row) => row!.role === "wing")
        || !parentRows.some((row) => row!.role === "fuselage")) {
      throw new Error("v5 transition source-surface chart is unavailable");
    }
    parents.forEach((parent) => {
      const role = occurrenceById.get(parent.occurrenceId)!.role;
      if (parent.patchId !== `${parent.occurrenceId}::${group.unionGroupId}:${role}-trimmed`) {
        throw new Error("v5 transition parent patch authority is not exact");
      }
    });
    const key = `${group.unionGroupId}:${parents.map(({ occurrenceId }) => occurrenceId).sort().join(":")}`;
    if (pairKeys.has(key)) throw new Error("v5 transition parent pair is duplicated");
    pairKeys.add(key);
  });
  const result: LoomAuthoritativeRebuildRequestV5 = {
    schema: LOOM_REBUILD_SCHEMA_V5,
    requestId: base.requestId,
    sourceDefinition: base.sourceDefinition,
    evaluatorId: LOOM_REBUILD_EVALUATOR_V5,
    topology: LOOM_REBUILD_TOPOLOGY_V5,
    definition: {
      schema: LOOM_REBUILD_DEFINITION_SCHEMA_V5,
      sourceDefinitions: base.definition.sourceDefinitions,
      occurrences: base.definition.occurrences,
      unionGroups: base.definition.unionGroups,
      transitions,
    },
  };
  if (new TextEncoder().encode(JSON.stringify(result)).byteLength > 1_052_672) {
    throw new Error("v5 request exceeds its serialized budget");
  }
  return result;
}

export function replaceLoomJunctionFairingStation(
  intent: LoomJunctionFairingIntent,
  stationId: string,
  update: Partial<Omit<LoomJunctionFairingStation, "stationId">>,
): LoomJunctionFairingIntent {
  return parseLoomJunctionFairingIntent({
    ...intent,
    construction: {
      ...intent.construction,
      stations: intent.construction.stations.map((station) => station.stationId === stationId
        ? { ...station, ...update } : station),
    },
  });
}
